import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  ArrowLeftRight,
  Loader2,
  Search,
  Filter,
  ArrowDownToLine,
  ArrowUpFromLine,
  Calendar,
  Download,
  ChevronLeft,
  ChevronRight,
  Package,
  Trash2,
  X
} from "lucide-react";

const MOTIVOS = {
  compra: "Compra",
  venta: "Venta",
  devolucion: "Devolución de cliente",
  devolucion_proveedor: "Devolución a proveedor",
  ajuste_positivo: "Ajuste positivo",
  ajuste_negativo: "Ajuste negativo",
  transferencia: "Transferencia",
  consumo: "Consumo interno",
  merma: "Merma / Pérdida",
  produccion: "Producción",
  donacion: "Donación",
  otro: "Otro"
};

export default function MovimientosInventarioPage() {
  const [movimientos, setMovimientos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [tipoFilter, setTipoFilter] = useState("all");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [page, setPage] = useState(0);
  const [deleting, setDeleting] = useState(null);
  const pageSize = 25;
  const { getAuthHeaders, user } = useAuth();
  
  // Check if user is admin
  const isAdmin = user?.role?.toLowerCase() === "admin" || user?.role?.toLowerCase() === "administrador";

  const fetchMovimientos = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("limit", pageSize);
      params.append("skip", page * pageSize);
      if (tipoFilter !== "all") params.append("tipo", tipoFilter);
      if (fechaDesde) params.append("fecha_desde", fechaDesde);
      if (fechaHasta) params.append("fecha_hasta", fechaHasta);

      const response = await axios.get(`${API}/inventario/movimientos?${params.toString()}`, getAuthHeaders());
      setMovimientos(response.data.movimientos || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error("Error fetching movements:", err);
      toast.error("Error al cargar movimientos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, page, tipoFilter, fechaDesde, fechaHasta]);

  useEffect(() => {
    fetchMovimientos();
  }, [fetchMovimientos]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleDateString("es", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-DO", { style: "currency", currency: "DOP" }).format(value || 0).replace("DOP", "RD$");
  };

  const exportToCSV = () => {
    if (movimientos.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }

    const headers = ["Fecha", "Tipo", "Artículo", "Código", "Cantidad", "Stock Anterior", "Stock Nuevo", "Motivo", "Referencia", "Usuario"];
    const rows = movimientos.map(mov => [
      formatDate(mov.created_at),
      mov.tipo === "entrada" ? "Entrada" : "Salida",
      mov.articulo?.nombre || "",
      mov.articulo?.codigo || "",
      mov.cantidad,
      mov.stock_anterior,
      mov.stock_nuevo,
      MOTIVOS[mov.motivo] || mov.motivo,
      mov.referencia || "",
      mov.created_by_name || ""
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${(cell || "").toString().replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `movimientos_inventario_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Archivo CSV descargado");
  };

  const handleDelete = async (movimiento) => {
    const confirmMsg = `¿Eliminar este movimiento?\n\nTipo: ${movimiento.tipo === "entrada" ? "Entrada" : "Salida"}\nArtículo: ${movimiento.articulo?.nombre}\nCantidad: ${movimiento.cantidad}\n\nEl stock del artículo será revertido automáticamente.`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      setDeleting(movimiento.id);
      await axios.delete(`${API}/inventario/movimientos/${movimiento.id}`, getAuthHeaders());
      toast.success("Movimiento eliminado y stock revertido");
      fetchMovimientos();
    } catch (err) {
      console.error("Error deleting:", err);
      toast.error(err.response?.data?.detail || "Error al eliminar movimiento");
    } finally {
      setDeleting(null);
    }
  };

  const filteredMovimientos = movimientos.filter(mov => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      mov.articulo?.nombre?.toLowerCase().includes(search) ||
      mov.articulo?.codigo?.toLowerCase().includes(search) ||
      mov.referencia?.toLowerCase().includes(search) ||
      mov.created_by_name?.toLowerCase().includes(search)
    );
  });

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="movimientos-inventario-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <ArrowLeftRight className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Movimientos de Inventario
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Historial de entradas y salidas
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={exportToCSV}>
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Buscar por artículo, código, referencia..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="w-48">
                <Select value={tipoFilter} onValueChange={(v) => { setTipoFilter(v); setPage(0); }}>
                  <SelectTrigger>
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los movimientos</SelectItem>
                    <SelectItem value="entrada">Solo Entradas</SelectItem>
                    <SelectItem value="salida">Solo Salidas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-40">
                <Label className="text-xs text-slate-500 mb-1 block">Fecha desde</Label>
                <Input
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => { setFechaDesde(e.target.value); setPage(0); }}
                  data-testid="fecha-desde-input"
                />
              </div>
              <div className="w-40">
                <Label className="text-xs text-slate-500 mb-1 block">Fecha hasta</Label>
                <Input
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => { setFechaHasta(e.target.value); setPage(0); }}
                  data-testid="fecha-hasta-input"
                />
              </div>
              {(fechaDesde || fechaHasta) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setFechaDesde(""); setFechaHasta(""); setPage(0); }}
                  className="text-slate-500"
                >
                  <X className="w-4 h-4 mr-1" />
                  Limpiar fechas
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <Card>
            <ScrollArea className="h-[calc(100vh-350px)]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Artículo</TableHead>
                    <TableHead className="text-right">Cantidad</TableHead>
                    <TableHead className="text-right">Stock Anterior</TableHead>
                    <TableHead className="text-right">Stock Nuevo</TableHead>
                    <TableHead>Motivo</TableHead>
                    <TableHead>Referencia</TableHead>
                    <TableHead>Usuario</TableHead>
                    {isAdmin && <TableHead className="text-right">Acciones</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMovimientos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 10 : 9} className="text-center py-12">
                        <Package className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                        <p className="text-slate-500">No se encontraron movimientos</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredMovimientos.map((mov) => (
                      <TableRow key={mov.id}>
                        <TableCell className="whitespace-nowrap">
                          <div className="flex items-center gap-1 text-sm">
                            <Calendar className="w-3 h-3 text-slate-400" />
                            {formatDate(mov.created_at)}
                          </div>
                        </TableCell>
                        <TableCell>
                          {mov.tipo === "entrada" ? (
                            <Badge className="bg-green-100 text-green-700 flex items-center gap-1 w-fit">
                              <ArrowDownToLine className="w-3 h-3" />
                              Entrada
                            </Badge>
                          ) : (
                            <Badge className="bg-orange-100 text-orange-700 flex items-center gap-1 w-fit">
                              <ArrowUpFromLine className="w-3 h-3" />
                              Salida
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{mov.articulo?.nombre || "—"}</p>
                            <p className="text-xs text-slate-500 font-mono">{mov.articulo?.codigo}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-bold ${mov.tipo === "entrada" ? "text-green-600" : "text-orange-600"}`}>
                            {mov.tipo === "entrada" ? "+" : "-"}{mov.cantidad}
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-slate-500">{mov.stock_anterior}</TableCell>
                        <TableCell className="text-right font-medium">{mov.stock_nuevo}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{MOTIVOS[mov.motivo] || mov.motivo}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{mov.referencia || "—"}</TableCell>
                        <TableCell className="text-sm text-slate-500">{mov.created_by_name || "—"}</TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(mov)}
                              disabled={deleting === mov.id}
                              data-testid={`delete-mov-${mov.id}`}
                            >
                              {deleting === mov.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollArea>

            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t">
              <p className="text-sm text-slate-500">
                Mostrando {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} de {total} movimientos
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-600">
                  Página {page + 1} de {totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
